<template>
  <el-dialog
    title="Manage Lines"
    width="60%"
    append-to-body
    :visible.sync="dialogVisible"
    :before-close="handleClose"
    :close-on-click-modal="false"
  >
    <div v-if="loading" v-loading="true" class="p-4" />

    <!-- No messaging service — picks MS first -->
    <div v-else-if="!hasMessagingService">
      <div v-if="messagingServices.length === 0" class="text-center p-4">
        <p>No eligible Messaging Services found for this company.</p>
      </div>

      <div v-else>
        <div class="mb-3">
          <p class="text-muted">
            Select a Messaging Service to assign to this RCS Sender. Only
            services with an approved A2P Use Case and at least one line are
            shown.
          </p>
        </div>

        <el-table
          :data="messagingServices"
          highlight-current-row
          style="width: 100%"
          @current-change="handleCurrentChange"
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column
            prop="friendly_name"
            label="Friendly Name"
            min-width="180"
            show-overflow-tooltip
          >
            <template v-slot="scope">
              {{ scope.row.friendly_name || '-' }}
            </template>
          </el-table-column>
          <el-table-column
            prop="sid"
            label="SID"
            min-width="220"
            show-overflow-tooltip
          >
            <template v-slot="scope">
              {{ scope.row.sid || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="Lines" min-width="200" show-overflow-tooltip>
            <template v-slot="scope">
              <span v-if="scope.row.lines && scope.row.lines.length > 0">
                {{ scope.row.lines.map(function(l) { return l.name }).join(', ') }}
              </span>
              <span v-else class="text-muted">No lines</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="d-flex justify-content-end mt-3">
          <el-pagination
            layout="sizes, prev, pager, next"
            :current-page.sync="pagination.page"
            :page-sizes="[10, 20, 50]"
            :page-size.sync="pagination.size"
            :total="pagination.total"
            :disabled="loading || submitting"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>

        <!-- Campaign selector (shown after MS is selected) -->
        <div v-if="availableLines.length > 0" class="mt-3">
          <label class="font-weight-bold">Select a Line (Campaign):</label>
          <el-select
            v-model="selectedCampaignId"
            placeholder="Select a line"
            class="w-100"
            filterable
          >
            <el-option
              v-for="line in availableLines"
              :key="line.id"
              :label="line.name"
              :value="line.id"
            />
          </el-select>
        </div>
      </div>
    </div>

    <!-- Mode B: Messaging service already set — admin picks campaign only -->
    <div v-else>
      <div v-if="availableLines.length === 0" class="text-center p-4">
        <p>No lines found for the associated Messaging Service.</p>
      </div>

      <div v-else>
        <div class="mb-3">
          <p class="text-muted">
            This RCS Sender already has a Messaging Service assigned. Select a
            line to associate with this RCS Sender.
          </p>
        </div>

        <label class="font-weight-bold">Select a Line (Campaign):</label>
        <el-select
          v-model="selectedCampaignId"
          placeholder="Select a line"
          class="w-100"
          filterable
        >
          <el-option
            v-for="line in availableLines"
            :key="line.id"
            :label="line.name"
            :value="line.id"
          />
        </el-select>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button
        type="primary"
        :loading="submitting"
        :disabled="!canSubmit || submitting"
        @click="handleSubmit"
      >
        Assign
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'RcsManageLinesDialog',
  props: {
    visible: { type: Boolean, default: false },
    rcsSender: { type: Object, default: null },
    company: { type: Object, required: true }
  },
  data() {
    return {
      messagingServices: [],
      selectedMessagingServiceId: null,
      selectedCampaignId: null,
      availableLines: [],
      loading: false,
      submitting: false,
      pagination: { page: 1, size: 10, total: 0 }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value) {
          this.handleClose()
        }
      }
    },
    hasMessagingService() {
      return this.rcsSender && !!this.rcsSender.messaging_service_id
    },
    canSubmit() {
      if (!this.selectedCampaignId) {
        return false
      }

      if (!this.hasMessagingService && !this.selectedMessagingServiceId) {
        return false
      }

      return true
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        if (this.hasMessagingService) {
          this.fetchLinesForMessagingService(
            this.rcsSender.messaging_service_id
          )
        } else {
          this.fetchMessagingServices()
        }
      } else {
        this.resetData()
      }
    }
  },
  methods: {
    fetchMessagingServices(page = 1) {
      this.loading = true
      this.pagination.page = page

      axios
        .get(`/api/admin/compliance/${this.company.id}/4`, {
          params: {
            page: this.pagination.page,
            size: this.pagination.size,
            lines: true
          }
        })
        .then((response) => {
          const data = response.data.bundles || response.data
          this.messagingServices = data.data || []
          this.pagination.total = data.total || 0
          this.pagination.page = data.current_page || 1
        })
        .catch((err) => {
          this.$root.handleErrors(err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchLinesForMessagingService(messagingServiceId) {
      this.loading = true

      axios
        .get(`/api/admin/compliance/${this.company.id}/4`, {
          params: {
            page: 1,
            size: 100,
            lines: true
          }
        })
        .then((response) => {
          const data = response.data.bundles || response.data
          const services = data.data || []
          const service = services.find((s) => s.id === messagingServiceId)
          this.availableLines = service ? service.lines || [] : []
        })
        .catch((err) => {
          this.$root.handleErrors(err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleCurrentChange(row) {
      this.selectedMessagingServiceId = row ? row.id : null
      this.selectedCampaignId = null
      this.availableLines = row ? row.lines || [] : []
    },

    handlePageChange(page) {
      this.fetchMessagingServices(page)
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.fetchMessagingServices(1)
    },

    async handleSubmit() {
      if (!this.canSubmit || !this.rcsSender) {
        return
      }

      this.submitting = true

      const payload = {
        campaign_id: this.selectedCampaignId
      }

      if (!this.hasMessagingService) {
        payload.messaging_service_id = this.selectedMessagingServiceId
      }

      await axios
        .post(
          `/api/admin/compliance/${this.company.id}/rcs-senders/${this.rcsSender.id}/assign-messaging-service`,
          payload
        )
        .then(() => {
          this.$notify({
            title: 'Success',
            message: 'Messaging Service assigned successfully.',
            type: 'success',
            showClose: true
          })
          this.$emit('assigned')
          this.handleClose()
        })
        .catch((err) => {
          this.$root.handleErrors(err.response)
        })
        .finally(() => {
          this.submitting = false
        })
    },

    resetData() {
      this.messagingServices = []
      this.selectedMessagingServiceId = null
      this.selectedCampaignId = null
      this.availableLines = []
      this.pagination.page = 1
    },

    handleClose() {
      this.resetData()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.dialog-footer {
    text-align: right;
}

::v-deep .el-table__body tr.current-row td {
  background-color: #cdcdcd !important;
}

::v-deep .el-table__body tr.current-row:hover td {
  background-color: #cdcdcd !important;
}
</style>
